/**
 * Shift a template schedule to a work date using wall time in a zone (Europe/Copenhagen).
 * For Media Devoted | Created By Ciro Autiero
 */

export const PRESET_SCHEDULE_TIMEZONE = 'Europe/Copenhagen';

function getPartsInZone(d: Date, timeZone: string): { y: number; mo: number; d: number; h: number; mi: number; s: number } {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  return {
    y: parseInt(get('year'), 10),
    mo: parseInt(get('month'), 10),
    d: parseInt(get('day'), 10),
    h: parseInt(get('hour'), 10),
    mi: parseInt(get('minute'), 10),
    s: parseInt(get('second'), 10),
  };
}

function getTimezoneOffsetHours(year: number, month: number, day: number, timeZone: string): number {
  const noonUtc = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(noonUtc);
  const hourPart = parts.find((p) => p.type === 'hour')?.value;
  if (!hourPart) return 0;
  const zoneHour = parseInt(hourPart, 10);
  return zoneHour - 12;
}

/** "YYYY-MM-DD" from work date string (validated minimally). */
function parseWorkDateYmd(workDateYmd: string): { y: number; mo: number; d: number } {
  const m = workDateYmd.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) throw new Error(`Invalid workDateYmd "${workDateYmd}"`);
  return { y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]) };
}

/**
 * Whole days between two calendar dates, via UTC-noon anchors.
 *
 * Noon, not midnight: a DST transition moves a day by an hour, and midnight
 * anchors put the two ends either side of it often enough to produce a
 * fractional day that floors to the wrong answer.
 */
function wholeDaysBetween(
  from: { y: number; mo: number; d: number },
  to: { y: number; mo: number; d: number },
): number {
  const a = Date.UTC(from.y, from.mo - 1, from.d, 12);
  const b = Date.UTC(to.y, to.mo - 1, to.d, 12);
  return Math.round((b - a) / 86_400_000);
}

/**
 * Whole days between a drop's work day and its planned send, in `zone`.
 *
 * The one definition of "the gap": a late send authored on the 13th and planned
 * for the 14th at 05:44 is 1. Used when copying a drop (where the source work
 * day is to hand) and when saving a preset (where it must be stored, because
 * the saved time keeps only the wall clock).
 */
export function scheduleDayOffsetFromWorkDate(
  workDateYmd: string,
  executeAfterIso: string,
  zone: string = PRESET_SCHEDULE_TIMEZONE,
): number {
  const at = new Date(executeAfterIso);
  if (Number.isNaN(at.getTime())) return 0;
  try {
    const { y, mo, d } = getPartsInZone(at, zone);
    return wholeDaysBetween(parseWorkDateYmd(workDateYmd), { y, mo, d });
  } catch {
    // An unparseable work date is not worth failing a save over; no gap is the
    // safe reading, and it is exactly what happened before this existed.
    return 0;
  }
}

/**
 * Takes hour/minute/second from templateAt in `zone`, applies to workDateYmd in same zone, returns UTC Date.
 * If result is before `nowUtc`, returns `nowUtc`.
 *
 * `sourceWorkDateYmd` preserves the DAY GAP. A prepared drop belongs to a work
 * day but is sent at an instant that may fall on the next one — a late local
 * send authored on the 13th is planned for the 14th at 05:44, and that +1 is
 * the point of it. Copying such a drop used to keep only the wall clock and
 * take the date wholly from the target day, silently pulling the send twelve
 * hours earlier and onto the wrong side of midnight for the recipient.
 *
 * Given the day the template belonged to, the same gap is re-applied to the
 * target. Omitted, the behaviour is exactly as before (gap of zero), which is
 * what every caller without a source day — a stored preset time, for instance —
 * still needs.
 */
export function shiftScheduleTemplateToWorkDate(params: {
  templateAt: Date;
  workDateYmd: string;
  /** The work day `templateAt` belonged to. Its gap to `templateAt` is kept. */
  sourceWorkDateYmd?: string;
  /** Days to add to the target work date. Ignored when `sourceWorkDateYmd` is given. */
  dayOffset?: number;
  zone?: string;
  nowUtc?: Date;
}): Date {
  const zone = params.zone ?? PRESET_SCHEDULE_TIMEZONE;
  const nowUtc = params.nowUtc ?? new Date();
  if (Number.isNaN(params.templateAt.getTime())) return nowUtc;

  const { y: ty, mo: tmo, d: td, h, mi, s } = getPartsInZone(params.templateAt, zone);
  const target = parseWorkDateYmd(params.workDateYmd);

  // The gap is measured in the SAME zone the wall clock is read in, or the two
  // disagree for any send within an offset's distance of midnight.
  const offsetDays = params.sourceWorkDateYmd
    ? wholeDaysBetween(parseWorkDateYmd(params.sourceWorkDateYmd), {
        y: ty,
        mo: tmo,
        d: td,
      })
    : (params.dayOffset ?? 0);

  // Resolve the landing day first — month/year roll over here — and read the
  // zone offset for THAT day. Reading it for the target day would be wrong
  // whenever the gap carries the send across a DST change.
  const landing = new Date(Date.UTC(target.y, target.mo - 1, target.d + offsetDays, 12));
  if (Number.isNaN(landing.getTime())) return nowUtc;
  const ly = landing.getUTCFullYear();
  const lmo = landing.getUTCMonth() + 1;
  const ld = landing.getUTCDate();

  const offsetHours = getTimezoneOffsetHours(ly, lmo, ld, zone);
  const utcDate = new Date(Date.UTC(ly, lmo - 1, ld, h - offsetHours, mi, s));
  if (Number.isNaN(utcDate.getTime())) return nowUtc;
  return utcDate.getTime() < nowUtc.getTime() ? nowUtc : utcDate;
}

/** Fixed calendar anchor for stored templates (only Copenhagen wall clock is meaningful on apply). */
const TEMPLATE_CLOCK_ANCHOR = { y: 2000, mo: 6, d: 15 } as const;

/**
 * Re-encode `sourceIso` to an ISO instant with the same Copenhagen H:M:S on a fixed anchor date.
 * Makes DB values clearly “time template” while apply still uses wall time only.
 */
export function normalizeScheduleTemplateWallClockIso(
  sourceIso: string,
  zone: string = PRESET_SCHEDULE_TIMEZONE,
): string {
  const trimmed = sourceIso.trim();
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return trimmed;
  const { h, mi, s } = getPartsInZone(d, zone);
  const { y, mo, d: day } = TEMPLATE_CLOCK_ANCHOR;
  const offsetHours = getTimezoneOffsetHours(y, mo, day, zone);
  const utcDate = new Date(Date.UTC(y, mo - 1, day, h - offsetHours, mi, s));
  return utcDate.toISOString();
}
